/**
 * The contract every external system plugs into.
 *
 * A provider module is the only thing in the app that knows what "Gmail" means:
 * which scopes it needs, what its natural subcollections are, how to ask it for
 * a date range, and how to turn what comes back into something the timeline can
 * render. Everything downstream sees only HGTimeRecordPlus.
 *
 * The server deliberately holds no copy of this. It stores `provider_key` and
 * `provider_config` as opaque strings, because a server-side catalogue would be
 * a second list of providers that is always one deploy behind this one.
 */

import { ComponentType } from "react";

import { HGIntervalType, HGRecordUrl } from "@/common/types";

export type ProviderConfig = Record<string, unknown>;

/** One item as the provider sees it, before it becomes a record. */
export interface ExternalItem {
  /** Stable within the provider; forms part of the synthetic record id. */
  externalId: string;
  title: string;
  content: string;
  /** When it happened. */
  at: Date;
  /**
   * The end of a span -- a ticket open→closed, a trip. Omit for a point event
   * like an email, which then occupies a single second.
   */
  until?: Date;
  url?: HGRecordUrl[];
}

/** How many items sit in a bucket too coarse to list individually. */
export interface ExternalSummary {
  count: number;
  /** Providers that only offer an approximation say so, and the row shows it. */
  estimate: boolean;
}

export interface ProviderFetchContext {
  accessToken: string;
  config: ProviderConfig;
  from: Date;
  to: Date;
  limit: number;
  signal: AbortSignal;
}

export interface ProviderChildTemplate {
  title: string;
  providerKey: string;
  config: ProviderConfig;
}

export interface ProviderLimits {
  /**
   * Nothing this provider knows about happened before here. Ranges entirely
   * outside it are never requested at all -- a Gmail collection viewed at the
   * Jurassic costs no network.
   */
  earliest?: Date;
  /**
   * Wider than this and the provider is asked for counts per bucket instead of
   * items. The bound is about how much a person can read, not what the API can
   * answer.
   */
  detailSpanDays: number;
  /** Hard cap on items in one fetch; the UI says when it was hit. */
  maxItems: number;
}

export interface ExternalProviderModule {
  /** Stored in collection.provider_key. */
  key: string;
  /** Which connection it fetches with; several providers may share one. */
  systemId: string;
  title: string;
  /** One line, shown in the create-collection library. */
  description: string;
  Icon: ComponentType<{ className?: string }>;
  scopes: string[];
  /**
   * Created as real collection rows alongside the root. Real rows rather than
   * virtual ones so the collection graph stays uniform -- the sidebar, pins,
   * slugs and traversals need no special case.
   */
  naturalChildren: ProviderChildTemplate[];
  limits: ProviderLimits;
  /**
   * Where the fetch runs. Only "client" is implemented: Google's APIs, GitHub
   * and GitLab all send CORS headers, so the browser can call them directly and
   * no user data touches our server. Atlassian's gateway does not, so the field
   * exists to give a future server-side proxy one place to branch rather than a
   * redesign.
   */
  transport?: "client" | "proxy";
  fetchItems(ctx: ProviderFetchContext): Promise<ExternalItem[]>;
  fetchSummary(ctx: ProviderFetchContext): Promise<ExternalSummary>;
  /** Permalink into the source system. External records are read-only here. */
  itemUrl(item: ExternalItem, config: ProviderConfig): string | undefined;
  /**
   * Coarsest interval worth grouping this provider's rows by, if it has an
   * opinion. Used only to pick summary buckets.
   */
  summaryFloor?: HGIntervalType;
}

/** Thrown when the grant is gone; surfaces as a "reconnect" prompt, not an error. */
export class ExternalAuthError extends Error {
  constructor(message = "This connection needs to be authorized again.") {
    super(message);
    this.name = "ExternalAuthError";
  }
}
